import React, { useState, useMemo, Fragment } from 'react'
import { Form, Alert, ProgressBar } from 'react-bootstrap'
import { uploadDocument } from '../../services/documentsApi'
import { CompanySearchModal } from '../Samples/CompanySearchModal'
import { ContactCompanySearchResult } from '../../models/companies'
import { FileDragDrop } from '../FileDragDrop'
import { Modal } from '../UI/Modal/Modal'
import { CreatableTagSelector } from '../CreatableTagSelector'
import { useForm, FormProvider } from 'react-hook-form'
import apiService from '../../services/api'
import { ProductSearchModal } from '../UI/ProductSearchModal'
import type { ProductVariantSelection } from '../UI/ProductSearchModal/types'
import { Select as UISelect } from '../UI/Select/Select'
import type { ISelectItem } from '../UI/Select/Select'
import { hasProductNoVariants } from '../../utils/productHelpers'
import FbButton from '../FbUI/FbButton'
import { Plus, X } from 'lucide-react'

const ALLOWED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.mp3',
  '.wav',
  '.aac',
  '.ogg',
  '.m4a',
  '.mp4',
  '.mov',
  '.webm',
  '.avi',
  '.mkv',
  '.m4v',
  '.wmv',
]

const MAX_FILE_SIZE = 524288000 // 500MB

interface UploadDocumentModalProps {
  // Controlled mode props (optional)
  isOpen?: boolean
  onClose?: () => void
  // Common props
  preSelectedCompanyId?: number
  preSelectedCompanyName?: string
  preSelectedProductId?: number
  preSelectedProductName?: string
  preSelectedVariantId?: number
  preSelectedVariantName?: string
  /** When true, product and variant selection is locked and cannot be changed */
  lockProductVariant?: boolean
  onSuccess?: () => void
}

interface FormValues {
  name: string
  tags: string[]
}

export const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  preSelectedCompanyId,
  preSelectedCompanyName,
  preSelectedProductId,
  preSelectedProductName,
  preSelectedVariantId,
  preSelectedVariantName,
  lockProductVariant = false,
  onSuccess,
}) => {
  // Use controlled mode if isOpen/onClose are provided, otherwise use internal state
  const [internalOpen, setInternalOpen] = useState(false)
  const open = isOpen !== undefined ? isOpen : internalOpen
  const setOpen =
    onClose !== undefined
      ? (value: boolean) => {
          if (!value) onClose()
        }
      : setInternalOpen

  const api = apiService()

  // Fetch products and document tags
  const { data: products, isLoading: isLoadingProducts } = api.useGetProducts()
  const { data: documentTags } = api.useGetTags('documents', !open)

  // Convert to options format for CreatableTagSelector
  const tagsOptions = useMemo(
    () =>
      documentTags?.map((tag) => ({
        label: tag.tag,
        value: tag.tag, // Use tag name as value for documents
      })) || [],
    [documentTags]
  )

  // Form state with react-hook-form
  const methods = useForm<FormValues>({
    defaultValues: {
      name: '',
      tags: [],
    },
  })

  // State
  const [file, setFile] = useState<File | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    preSelectedProductId?.toString() ?? null
  )
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    preSelectedVariantId ?? null
  )
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(
    preSelectedVariantName ?? null
  )
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [selectedCompany, setSelectedCompany] =
    useState<ContactCompanySearchResult | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCompanySearch, setShowCompanySearch] = useState(false)

  // File validation helper
  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large. Maximum size is 500MB.`
    }

    // Check file extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`
    }

    return null
  }

  // File change handler
  const handleFileChange = (selectedFile: File | null) => {
    if (!selectedFile) {
      setFile(null)
      setError(null)
      return
    }

    const validationError = validateFile(selectedFile)
    if (validationError) {
      setError(validationError)
      setFile(null)
      return
    }

    setFile(selectedFile)
    setError(null)
    if (!methods.getValues('name')) {
      methods.setValue('name', selectedFile.name)
    }
  }

  // Reset form
  const handleClose = () => {
    setFile(null)
    methods.reset()
    setSelectedProductId(preSelectedProductId?.toString() ?? null)
    setSelectedVariantId(preSelectedVariantId ?? null)
    setSelectedVariantName(preSelectedVariantName ?? null)
    setSelectedCompany(null)
    setUploadProgress(0)
    setError(null)
    setOpen(false)
  }

  // Upload handler
  const handleUpload = async () => {
    if (!file) return

    setIsUploading(true)
    setError(null)
    setUploadProgress(0)

    try {
      const formValues = methods.getValues()

      await uploadDocument(
        {
          file,
          name: formValues.name || file.name,
          tags: formValues.tags || [],
          // Only send product_id if variant_id is not set (API doesn't allow both)
          product_id:
            !selectedVariantId && selectedProductId
              ? Number(selectedProductId)
              : undefined,
          variant_id: selectedVariantId || undefined,
          contact_company_id:
            selectedCompany?.contact_company_id || preSelectedCompanyId,
        },
        (progress) => {
          setUploadProgress(progress)
        }
      )

      onSuccess?.()
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Upload failed')
    } finally {
      setIsUploading(false)
    }
  }

  // Convert products to dropdown items
  const productItems: ISelectItem[] = useMemo(
    () =>
      products?.map((p) => ({
        key: p.id.toString(),
        label: p.name,
        value: p.id.toString(),
      })) || [],
    [products]
  )

  // Get selected product
  const selectedProduct = useMemo(
    () => products?.find((p) => p.id.toString() === selectedProductId),
    [products, selectedProductId]
  )

  // Check if product has no variants
  const productHasNoVariants = hasProductNoVariants(products, selectedProductId)

  // Handle variant selection from modal
  const handleVariantSelect = (selection: ProductVariantSelection) => {
    if (selection.variants && selection.variants.length > 0) {
      const variant = selection.variants[0]
      setSelectedVariantId(variant.id)
      setSelectedVariantName(variant.name)
    }
    setShowVariantModal(false)
  }

  // Handle product selection
  const handleProductSelect = (productId: string | null) => {
    setSelectedProductId(productId)
    // Clear variant when product changes
    setSelectedVariantId(null)
    setSelectedVariantName(null)
  }

  // Clear product and variant
  const handleClearProduct = () => {
    setSelectedProductId(null)
    setSelectedVariantId(null)
    setSelectedVariantName(null)
  }

  // Determine if we should show the trigger button (only in uncontrolled mode)
  const showTriggerButton = isOpen === undefined && onClose === undefined

  return (
    <Fragment>
      {showTriggerButton && (
        <FbButton onClick={() => setInternalOpen(true)}>
          Upload Document
        </FbButton>
      )}
      <FormProvider {...methods}>
        <Modal
          open={open}
          onOpenChange={(open) => {
            if (!open) {
              methods.reset()
              setOpen(open)
            }
          }}
          size="lg"
          title="Upload Document"
          onAccept={handleUpload}
          acceptButtonText="Upload"
          blockAccept={!file || isUploading}
          loading={isUploading}
          onCancel={handleClose}
          cancelButtonText="Cancel"
        >
          <div className="space-y-4">
            {/* Drag & Drop Zone */}
            <FileDragDrop
              file={file}
              onFileChange={handleFileChange}
              supportText={`Max 500MB\n${ALLOWED_EXTENSIONS.map((ext) => ext.replace('.', '')).join(', ')}`}
            />

            {/* Name Field */}
            <Form.Group>
              <Form.Label className="text-base font-medium text-gray-800 select-none mb-1">
                Document Name
              </Form.Label>
              <Form.Control
                type="text"
                {...methods.register('name')}
                placeholder="Auto-filled from filename"
              />
            </Form.Group>

            {/* Tag Selector */}
            <CreatableTagSelector
              name="tags"
              control={methods.control}
              placeholder="Tags"
              options={tagsOptions}
              setError={methods.setError}
              tagParent="documents"
              label="Tags"
              optional
            />

            {/* Product & Variant Selector */}
            <div className="mt-4 space-y-3">
              <h3 className="text-base font-medium text-gray-800 select-none mb-1">
                Product & Variant
                {!lockProductVariant && (
                  <span className="text-sm italic font-normal">
                    {' '}
                    - optional
                  </span>
                )}
              </h3>

              {/* Locked view - show read-only product and variant */}
              {lockProductVariant &&
                (preSelectedProductName || preSelectedVariantName) && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    {preSelectedProductName && (
                      <div className="font-medium text-gray-900">
                        {preSelectedProductName}
                      </div>
                    )}
                    {preSelectedVariantName && (
                      <div className="text-sm text-gray-600 mt-1">
                        Variant: {preSelectedVariantName}
                      </div>
                    )}
                  </div>
                )}

              {/* Editable view - Product Dropdown - Hide when product is selected or locked */}
              {!lockProductVariant && !selectedProductId && (
                <div>
                  <UISelect
                    items={productItems}
                    selected={selectedProductId}
                    onSelect={handleProductSelect}
                    nullSelectionLabel="No product selected"
                    buttonVariant="secondary"
                    buttonClassName="w-full"
                    searchPlaceholder="Search products..."
                    disabled={isLoadingProducts}
                    matchTriggerWidth
                  />
                </div>
              )}

              {/* Editable view - Show selection & variant button if product is selected */}
              {!lockProductVariant && selectedProductId && selectedProduct && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {selectedProduct.name}
                      </div>
                      {selectedVariantName && (
                        <div className="text-sm text-gray-600 mt-1">
                          Variant: {selectedVariantName}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleClearProduct}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Clear product"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  {/* Show variant button only if product has variants */}
                  {!productHasNoVariants && (
                    <button
                      type="button"
                      onClick={() => setShowVariantModal(true)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md border-[1px] border-[#2C6B4E] bg-[#EEF5F2] hover:bg-[#E5F2EA] transition-colors"
                    >
                      <Plus size={16} className="text-[#06512F]" />
                      <span className="font-medium text-sm leading-none text-[#06512F]">
                        {selectedVariantId
                          ? 'Change Variant'
                          : 'Select Variant (Optional)'}
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Company Search */}
            {!preSelectedCompanyId && (
              <div className="mt-4">
                <h3 className="text-base font-medium text-gray-800 select-none mb-1">
                  Company
                  <span className="text-sm italic font-normal">
                    {' '}
                    - optional
                  </span>
                </h3>
                {selectedCompany ? (
                  <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4 bg-white">
                    <span className="text-gray-900">
                      {selectedCompany.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedCompany(null)}
                      className="text-sm text-new-company-green-active hover:text-new-company-green-dark hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowCompanySearch(true)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md border-[1px] border-[#2C6B4E] bg-[#EEF5F2] hover:bg-[#E5F2EA] transition-colors"
                  >
                    <Plus size={16} className="text-[#06512F]" />
                    <span className="font-medium text-sm leading-none text-[#06512F]">
                      Select Company
                    </span>
                  </button>
                )}
              </div>
            )}

            {preSelectedCompanyId && (
              <div className="mt-4">
                <h3 className="text-base font-medium mb-3">Company</h3>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <span className="text-gray-900">
                    {preSelectedCompanyName ||
                      `Company ID: ${preSelectedCompanyId}`}
                  </span>
                </div>
              </div>
            )}

            {/* Progress Bar */}
            {isUploading && (
              <div>
                <ProgressBar
                  now={uploadProgress}
                  label={`${uploadProgress}%`}
                  animated
                />
              </div>
            )}

            {/* Error Display */}
            {error && <Alert variant="danger">{error}</Alert>}
          </div>
        </Modal>

        {/* Company Search Modal */}
        <CompanySearchModal
          open={showCompanySearch}
          onClose={() => setShowCompanySearch(false)}
          onSelectCompany={(company) => {
            setSelectedCompany(company)
            setShowCompanySearch(false)
          }}
        />

        {/* Variant Search Modal */}
        <ProductSearchModal
          show={showVariantModal}
          onHide={() => setShowVariantModal(false)}
          selectionMode="variants-only"
          onSelect={handleVariantSelect}
          title="Select Variant"
          showProductFilter={false}
          multiSelect={false}
          maxSelections={1}
          restrictToProduct={
            selectedProductId ? Number(selectedProductId) : undefined
          }
        />
      </FormProvider>
    </Fragment>
  )
}
